import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MailQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Brand";
import { CONTACT_URL } from "@/lib/brand";

const REASONS = {
  not_invited: {
    title: "That account is not on the list",
    body: "Your Google account signed in correctly, but this editor only lets in email addresses your agency has invited. Nothing has been created for that account.",
  },
  revoked: {
    title: "Your access has been turned off",
    body: "This email address was invited before but is no longer on the list. Your agency can turn it back on.",
  },
  no_client: {
    title: "No website assigned yet",
    body: "Your account is invited, but no website has been assigned to it. Your agency needs to finish setting this up.",
  },
  state: {
    title: "That sign-in could not be completed",
    body: "The sign-in took too long or was interrupted. Please start again.",
  },
  expired: {
    title: "That sign-in link expired",
    body: "Sign-in attempts are only valid for a few minutes. Please start again.",
  },
  verify: {
    title: "Google could not confirm that account",
    body: "We could not verify the account with Google. Please try again, and make sure your email address is verified with Google.",
  },
  cancelled: {
    title: "Sign-in was cancelled",
    body: "You closed the Google window before finishing. No harm done.",
  },
  session: {
    title: "That sign-in could not be completed",
    body: "The sign-in link had already been used or had expired. Sign-in links are good for one use only. Please start again.",
  },
};

export default function NotInvited() {
  const [params] = useSearchParams();
  const reason = params.get("reason") || "not_invited";
  const email = params.get("email");
  const copy = REASONS[reason] || REASONS.not_invited;

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="rp-brand-panel rp-noise relative overflow-hidden rounded-t-2xl p-6">
          <Wordmark size="md" />
        </div>
        <div className="rounded-b-2xl border border-t-0 border-[hsl(var(--border))] bg-white p-6 sm:p-8">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--rp-navy))]">
            <MailQuestion className="h-5 w-5" aria-hidden="true" />
          </span>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight" data-testid="not-invited-title">
            {copy.title}
          </h1>
          <p className="mt-3 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{copy.body}</p>
          {email ? (
            <p className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 font-mono text-xs">
              {email}
            </p>
          ) : null}
          <p className="mt-6 text-sm leading-6">
            If you should have access, ask us to invite this email address &mdash; it takes a few
            seconds.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="outline" className="bg-white" data-testid="back-to-login">
              <Link to="/login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Link>
            </Button>
            <Button
              asChild
              className="bg-[hsl(var(--rp-navy))] hover:bg-[hsl(var(--rp-navy-2))]"
              data-testid="not-invited-contact-link"
            >
              <a href={CONTACT_URL} target="_blank" rel="noreferrer noopener">
                <MailQuestion className="mr-2 h-4 w-4" />
                Contact Review Presence
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
